import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCreditsCached, clearCreditsCache } from '../services/credits';
import { API_ENDPOINTS } from '../config/api';

export default function HeaderCredits({ isAuthenticated = false }) {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trialTimeRemaining, setTrialTimeRemaining] = useState(null);
  const [subscribedUsersCount, setSubscribedUsersCount] = useState(null);
  const navigate = useNavigate();
  const SUBSCRIBED_USERS_BASE_COUNT = 2021;
  const totalSubscribedUsers = subscribedUsersCount === null ? 0 : SUBSCRIBED_USERS_BASE_COUNT + subscribedUsersCount;

  useEffect(() => {
    const fetchCredits = async () => {
      setLoading(true);
      try {
        const data = await fetchCreditsCached();
        setCredits(data.totalCredits ?? data.credits ?? 0);
      } catch {
        setCredits(0);
      } finally {
        setLoading(false);
      }
    };

    const fetchSubscribedUsersCount = async () => {
      setSubscribedUsersCount(null);
      try {
        console.log('Fetching subscribed users count from:', API_ENDPOINTS.SUBSCRIPTIONS.COUNT);
        const response = await fetch(API_ENDPOINTS.SUBSCRIPTIONS.COUNT, {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        });
        
        console.log('Subscriptions API response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('Subscribed users data:', data);
          const count = data.count ?? data.total ?? 0;
          console.log('Setting subscribed users count to:', count);
          setSubscribedUsersCount(count);
        } else {
          console.warn('Subscriptions API returned non-OK status:', response.status, '- using default count 0');
          setSubscribedUsersCount(null);
        }
      } catch (error) {
        console.error('Failed to fetch subscribed users count:', error, '- using default count 0');
        setSubscribedUsersCount(null);
      }
    };

    const handleCreditsUpdated = (evt) => {
      if (typeof evt?.detail?.totalCredits === 'number') {
        setCredits(evt.detail.totalCredits);
      } else if (typeof evt?.detail?.credits === 'number') {
        setCredits(evt.detail.credits);
      } else {
        // Clear cache and refetch
        clearCreditsCache();
        fetchCredits();
      }
    };
    
    const handleAuthChanged = () => {
      // When auth changes, clear cache and refetch credits
      clearCreditsCache();
      fetchCredits();
    };
    
    if (isAuthenticated) {
      fetchCredits();
    }
    
    // Always fetch subscribed users count (public API)
    fetchSubscribedUsersCount();
    
    // Always listen for credits and auth updates
    window.addEventListener('creditsUpdated', handleCreditsUpdated);
    window.addEventListener('authChanged', handleAuthChanged);
    window.addEventListener('authHydrated', handleAuthChanged);

    return () => {
      window.removeEventListener('creditsUpdated', handleCreditsUpdated);
      window.removeEventListener('authChanged', handleAuthChanged);
      window.removeEventListener('authHydrated', handleAuthChanged);
    };
  }, [isAuthenticated]);

  // Monitor active trial session
  useEffect(() => {
    // Don't show trial in local development
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (isLocalDev) {
      setTrialTimeRemaining(null);
      return;
    }

    const checkTrialSession = () => {
      const sessionEndTime = localStorage.getItem('session_end_time');
      const interviewConfig = localStorage.getItem('interview_config');
      
      if (sessionEndTime && interviewConfig) {
        try {
          const config = JSON.parse(interviewConfig);
          if (config.isTrial === true) {
            const remaining = Math.floor((parseInt(sessionEndTime) - Date.now()) / 1000);
            if (remaining > 0) {
              setTrialTimeRemaining(remaining);
              return;
            } else {
              // Trial ended
              setTrialTimeRemaining(null);
              localStorage.removeItem('session_end_time');
            }
          }
        } catch (e) {
          // Invalid config
        }
      }
      setTrialTimeRemaining(null);
    };

    checkTrialSession();
    const interval = setInterval(checkTrialSession, 1000);

    // Listen for storage changes (e.g., trial restored after login)
    const handleStorageChange = () => checkTrialSession();
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('trialSessionRestored', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('trialSessionRestored', handleStorageChange);
    };
  }, []);

  // Format time for display (mm:ss)
  const formatTrialTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      background: 'transparent',
      borderRadius: 0,
      padding: 0,
      fontWeight: 600,
      fontSize: 'clamp(12px, 2.5vw, 15px)',
      color: '#fff',
      boxShadow: 'none',
      border: 'none',
      marginLeft: 16,
      flexWrap: 'wrap',
      justifyContent: 'flex-end'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: 'rgba(37, 99, 235, 0.1)',
        border: '1px solid rgba(37, 99, 235, 0.3)',
        borderRadius: 6,
        padding: '6px 12px',
        whiteSpace: 'nowrap'
      }}>
        <span style={{ color: '#93c5fd', fontWeight: 500, fontSize: 'clamp(11px, 2.3vw, 13px)' }}>
          👥 Total Subscribed:
        </span>
        <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
          {totalSubscribedUsers.toLocaleString()}
        </span>
      </div>
      {isAuthenticated && (
        <a
          href="https://wa.me/917259730473?text=Hi%20Team%2C%0AI%20would%20like%20to%20request%20a%20demo%20of%20the%20desktop%20or%20web%20application%20to%20use%20during%20an%20interview."
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: 'rgba(37, 211, 102, 0.15)',
            border: '1px solid rgba(37, 211, 102, 0.5)',
            borderRadius: 6,
            padding: '6px 12px',
            whiteSpace: 'nowrap',
            textDecoration: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" fill="#25D366"/>
          </svg>
          <span style={{ color: '#25d366', fontWeight: 600, fontSize: 'clamp(11px, 2.3vw, 13px)' }}>
            WhatsApp Us
          </span>
        </a>
      )}
      {trialTimeRemaining !== null && (
        <div
          onClick={() => navigate('/chat')}
          style={{
            background: 'rgba(72, 208, 42, 0.15)',
            border: '2px solid #48d02a',
            borderRadius: 8,
            padding: '6px 12px',
            fontWeight: 700,
            fontSize: 'clamp(12px, 2.5vw, 14px)',
            color: '#48d02a',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            animation: 'pulse 2s ease-in-out infinite',
            boxShadow: '0 2px 8px rgba(72, 208, 42, 0.3)'
          }}
        >
          ⏱ Trial: {formatTrialTime(trialTimeRemaining)}
        </div>
      )}
      {isAuthenticated && (
        <>
          <span style={{ 
            color: 'var(--brand-green)', 
            fontWeight: 700,
            whiteSpace: 'nowrap'
          }}>
            Interview Credits
          </span>
          <span style={{ 
            color: '#fff', 
            fontWeight: 700, 
            marginLeft: 4,
            whiteSpace: 'nowrap'
          }}>
            {loading ? '...' : credits !== null ? credits : 0}
          </span>
        </>
      )}
      <button
        style={{
          marginLeft: isAuthenticated ? 16 : 0,
          background: '#2563eb',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          padding: 'clamp(6px, 1.5vw, 6px) clamp(12px, 3vw, 16px)',
          fontWeight: 600,
          fontSize: 'clamp(12px, 2.5vw, 14px)',
          cursor: 'pointer',
          boxShadow: '0 1px 4px rgba(37,99,235,0.10)',
          whiteSpace: 'nowrap'
        }}
        onClick={() => navigate('/buy-credits')}
      >
        Get Credits
      </button>
    </div>
  );
}
